// Helpers for scheduling a campaign in a specific IANA timezone.
//
// The offset of a zone depends on the instant, not on today: Asia/Kolkata is
// always +05:30, but Europe/London is +00:00 in January and +01:00 in July. So a
// send time must be resolved against the scheduled date, never against `new Date()`.

/**
 * The audience-timezones API buckets contacts it cannot resolve a real IANA zone
 * for (e.g. Mobile Subscribers with no geo/carrier timezone data) under this literal
 * string rather than omitting them. It is not a zone Intl can resolve, so every
 * function below treats it as a fixed +00:00 offset — the admin's picked date/time
 * is used exactly as entered, with no zone conversion applied.
 */
export const NO_TIMEZONE = 'No/Tz';

const dtfCache = new Map();

const getDtf = (timeZone) => {
  if (!dtfCache.has(timeZone)) {
    dtfCache.set(
      timeZone,
      new Intl.DateTimeFormat('en-US', {
        timeZone,
        hour12: false,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    );
  }
  return dtfCache.get(timeZone);
};

// The wall-clock reading of `date` in `timeZone`, expressed as parts.
const zonedParts = (date, timeZone) => {
  const map = {};
  for (const p of getDtf(timeZone).formatToParts(date)) {
    if (p.type !== 'literal') map[p.type] = p.value;
  }
  // some engines report midnight as hour 24 under hour12: false
  const hour = Number(map.hour) % 24;
  return {
    year: Number(map.year),
    month: Number(map.month),
    day: Number(map.day),
    hour,
    minute: Number(map.minute),
    second: Number(map.second),
  };
};

/** Offset of `timeZone` at the given instant, in ms (+05:30 -> 19800000). */
export const getZoneOffsetMs = (date, timeZone) => {
  if (timeZone === NO_TIMEZONE) return 0;
  const p = zonedParts(date, timeZone);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  // strip sub-second noise so the value stays a clean minute offset
  return asUtc - Math.floor(date.getTime() / 1000) * 1000;
};

/**
 * "2026-08-27" + "12:00" as read on a clock in `timeZone` -> the real instant.
 * Resolved twice because the first offset guess can sit on the wrong side of a
 * DST transition.
 */
export const zonedTimeToUtc = (dateStr, timeStr, timeZone) => {
  if (!dateStr || !timeStr || !timeZone) return null;
  const [y, m, d] = String(dateStr).split('-').map(Number);
  const [hh, mm] = String(timeStr).split(':').map(Number);
  if ([y, m, d, hh, mm].some((n) => Number.isNaN(n))) return null;

  const naive = Date.UTC(y, m - 1, d, hh, mm, 0);
  let instant = naive - getZoneOffsetMs(new Date(naive), timeZone);
  instant = naive - getZoneOffsetMs(new Date(instant), timeZone);
  const result = new Date(instant);
  return Number.isNaN(result.getTime()) ? null : result;
};

/** Current wall clock in a zone, ready for <input type="date"> / <input type="time">. */
export const nowInZone = (timeZone) => {
  if (timeZone === NO_TIMEZONE) return nowInZone('UTC');
  const p = zonedParts(new Date(), timeZone);
  const pad = (n) => String(n).padStart(2, '0');
  return {
    date: `${p.year}-${pad(p.month)}-${pad(p.day)}`,
    time: `${pad(p.hour)}:${pad(p.minute)}`,
  };
};

/** "27 Aug 2026, 17:30" for an instant as seen in a zone. */
export const formatInZone = (date, timeZone) => {
  if (!date) return '';
  try {
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: timeZone === NO_TIMEZONE ? 'UTC' : timeZone,
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(date);
  } catch (e) {
    return date.toISOString();
  }
};

/** "GMT+05:30" for a zone, at an instant (defaults to now). */
export const zoneOffsetLabel = (timeZone, at = new Date()) => {
  if (timeZone === NO_TIMEZONE) return 'GMT+00:00';
  try {
    return (
      new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'longOffset' })
        .formatToParts(at)
        .find((p) => p.type === 'timeZoneName')?.value || ''
    );
  } catch (e) {
    return '';
  }
};

// A schedule this close to now cannot realistically be dispatched.
export const MIN_LEAD_MS = 60 * 1000;

/**
 * Is this local date+time still in the future in that zone?
 * Returns { valid, instant, reason } so callers can show the resolved instant.
 */
export const validateZonedSchedule = (dateStr, timeStr, timeZone, minLeadMs = MIN_LEAD_MS) => {
  if (!dateStr || !timeStr) return { valid: false, instant: null, reason: 'Pick a date and time' };
  const instant = zonedTimeToUtc(dateStr, timeStr, timeZone);
  if (!instant) return { valid: false, instant: null, reason: 'Invalid date or time' };
  if (instant.getTime() - Date.now() < minLeadMs) {
    const local = nowInZone(timeZone);
    const zoneLabel = timeZone === NO_TIMEZONE ? 'UTC (no timezone on file)' : timeZone;
    return {
      valid: false,
      instant,
      reason: `Already passed — it is ${local.time} on ${local.date} in ${zoneLabel}`,
    };
  }
  return { valid: true, instant, reason: '' };
};
